import { useCallback, useRef, useState } from 'react';

/**
 * Who a step acts as, rendered the same way everywhere it appears.
 *
 * Reef's whole access story turns on this distinction — a comment from your
 * account and a comment from the workspace bot are different acts with
 * different consequences — so it gets one vocabulary rather than being
 * re-described per screen. Three states, three treatments:
 *
 *   person   filled, blue, person glyph      — a named, accountable human
 *   bot      dashed outline, violet, bot glyph — a shared workspace credential
 *   auto     dotted outline, neutral         — depends who starts the run
 *
 * `auto` deliberately does not borrow either look. Showing it as "you" would be
 * wrong for every unattended run and vice versa; the honest rendering is a
 * third thing that says the answer is not fixed yet.
 */
export type IdentityKind = 'person' | 'bot' | 'auto';

/**
 * Credential modes that hold both a personal and a workspace credential, and so
 * have an identity worth choosing. Everything else resolves one way only.
 */
export const DUAL_IDENTITY = new Set(['user_or_service', 'github_app']);

/** What the bot half of a connection is called, for use mid-sentence. */
export function botLabelFor(connection: { name: string; serviceAccountLabel?: string | null }): string {
  return connection.serviceAccountLabel ?? `the ${connection.name} bot`;
}

/** Map the wire values — actor on a step, identity kind on an audit row. */
export function identityKindFor(value: string | null | undefined): IdentityKind {
  if (value === 'user') return 'person';
  if (value === 'service' || value === 'github_app' || value === 'system') return 'bot';
  return 'auto';
}

const GLYPHS: Record<IdentityKind, React.ReactNode> = {
  person: (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="5" r="3" />
      <path d="M2.5 14a5.5 5.5 0 0 1 11 0z" />
    </svg>
  ),
  bot: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M8 1.5v2" strokeLinecap="round" />
      <rect x="2.5" y="3.5" width="11" height="9" rx="2.5" />
      <circle cx="6" cy="8" r="1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="8" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  auto: (
    // The two glyphs side by side rather than a fork or an arrow: at 12px an
    // abstract mark reads as a stray letter, whereas "a person and a bot" is
    // still legible as two things, which is exactly what `auto` means.
    <svg viewBox="0 0 20 16" fill="currentColor" aria-hidden="true" style={{ width: '1.1em' }}>
      <circle cx="4.5" cy="6" r="2.6" />
      <path d="M0.5 14a4 4 0 0 1 8 0z" />
      <rect
        x="11"
        y="4.5"
        width="8"
        height="7.5"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.2 1.6"
      />
    </svg>
  ),
};

export function IdentityBadge({
  kind,
  label,
  tip,
  size = 'sm',
}: {
  kind: IdentityKind;
  label: string;
  /** The sentence explaining what this means for the reader. */
  tip?: string;
  size?: 'sm' | 'lg';
}) {
  const badge = (
    <span className={`identity identity--${kind}${size === 'lg' ? ' identity--lg' : ''}`}>
      {GLYPHS[kind]}
      {label}
    </span>
  );

  return tip ? <Tooltip text={`${label}. ${tip}`}>{badge}</Tooltip> : badge;
}

/**
 * A hover-and-focus tooltip positioned in the viewport.
 *
 * Measured and drawn at fixed coordinates rather than as a CSS pseudo-element,
 * because these badges sit inside cards and tables that clip their overflow —
 * the pure-CSS version is invisible in most of the places it is used here.
 */
export function Tooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  const open = useCallback(() => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    // Above the trigger by default; flip below when there is no room, so a
    // badge near the top of the viewport does not get a clipped tooltip.
    const above = box.top > 90;
    setAt({
      top: above ? box.top - 8 : box.bottom + 8,
      left: Math.max(8, Math.min(box.left, window.innerWidth - 328)),
    });
  }, []);

  const close = useCallback(() => setAt(null), []);

  return (
    <span
      ref={ref}
      className="tip-trigger"
      tabIndex={0}
      // The full sentence goes to assistive tech directly; the popup is the
      // sighted equivalent, not an extra thing to discover.
      aria-label={text}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      onKeyDown={(event) => event.key === 'Escape' && close()}
    >
      {children}
      {at && (
        <span
          className="tip-bubble"
          role="tooltip"
          style={{ top: at.top, left: at.left, transform: at.top < 90 ? undefined : 'translateY(-100%)' }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

/**
 * The wording used wherever an identity is explained, kept in one place so the
 * builder, the Connections screen and the audit log describe the same thing the
 * same way.
 */
export function identityTip(kind: IdentityKind, botName: string): string {
  switch (kind) {
    case 'person':
      return `Runs on that person's own account, so the work carries their name and respects their permissions. A run nobody triggered will stop and ask for a connection rather than falling back to ${botName}.`;
    case 'bot':
      return `Runs on the shared workspace credential, so the work appears as ${botName} rather than any individual — and keeps working when nobody is signed in.`;
    case 'auto':
      return `Decided when the run starts: the person who triggered it, or ${botName} when a schedule or a webhook did.`;
  }
}
